import React, { useMemo } from "react";
import { Form } from "react-bootstrap";
import SearchableListPicker, { SearchableOption } from "@core/components/SearchableListPicker";
import InfoTooltip from "@core/components/InfoTooltip";
import { useChildEntities } from "@features/admin/invitations/hooks/useChildEntities";
import {
    INVITE_DESTINATION_HINT,
    encodeInviteDestination,
    inviteChildGroupLabel,
    inviteScopeGroupLabel,
    parseInviteDestination,
} from "@features/admin/invitations/constants/inviteModal";
import { getApiScopeFromValue, getChildScope, getScopeFromValue } from "@features/invitations/utils/getScopeFromParam";

export interface InviteDestinationChange {
    scope: string;
    scopeRefId: number;
    name?: string;
}

interface InviteDestinationFieldProps {
    ownScopeValue: string;
    ownScopeRefId: number;
    ownLabel: string;
    /** Encoded destination value (SCOPE:id). */
    value: string;
    onChange: (next: InviteDestinationChange) => void;
    enabled?: boolean;
    isInvalid?: boolean;
    errorText?: string;
}

/**
 * Invite-to control: read-only at church; otherwise one grouped searchable picker
 * (own level + children). Child list is fetched once here.
 */
const InviteDestinationField: React.FC<InviteDestinationFieldProps> = ({
    ownScopeValue,
    ownScopeRefId,
    ownLabel,
    value,
    onChange,
    enabled = true,
    isInvalid = false,
    errorText,
}) => {
    const ownScope = useMemo(() => getScopeFromValue(ownScopeValue), [ownScopeValue]);
    const ownApiScope = useMemo(() => getApiScopeFromValue(ownScopeValue), [ownScopeValue]);
    const childScope = useMemo(() => getChildScope(ownScopeValue), [ownScopeValue]);

    const { options: childOptions, loading: childLoading } = useChildEntities(
        ownApiScope,
        ownScopeRefId,
        enabled && !!childScope,
        true
    );

    const options: SearchableOption[] = useMemo(() => {
        if (!ownScope || !(ownScopeRefId > 0)) return [];
        const ownGroup = inviteScopeGroupLabel(ownScope);
        const list: SearchableOption[] = [
            {
                value: encodeInviteDestination(ownScope, ownScopeRefId),
                label: ownLabel,
                group: ownGroup,
            },
        ];
        if (childScope && childOptions.length > 0) {
            const childGroup = inviteChildGroupLabel(childScope);
            for (const child of childOptions) {
                list.push({
                    value: encodeInviteDestination(childScope, Number(child.value)),
                    label: child.label,
                    group: childGroup,
                });
            }
        }
        return list;
    }, [ownScope, ownScopeRefId, ownLabel, childScope, childOptions]);

    const readOnly = !childScope;
    const ownEncoded =
        ownScope && ownScopeRefId > 0 ? encodeInviteDestination(ownScope, ownScopeRefId) : "";

    return (
        <Form.Group controlId="invTargetScope" data-testid="invite-destination-field">
            <Form.Label>
                Invite to<span className="text-danger">*</span>
                {!readOnly && <InfoTooltip text={INVITE_DESTINATION_HINT} />}
            </Form.Label>
            {readOnly ? (
                <Form.Control
                    type="text"
                    value={ownLabel}
                    disabled
                    data-testid="invite-destination-readonly"
                />
            ) : (
                <SearchableListPicker
                    options={options}
                    value={value || ownEncoded}
                    onChange={(nextValue) => {
                        const parsed = parseInviteDestination(nextValue);
                        if (!parsed) return;
                        const name = options.find((o) => String(o.value) === String(nextValue))?.label;
                        onChange({ ...parsed, name });
                    }}
                    placeholder="Select destination"
                    searchPlaceholder="Search…"
                    loading={childLoading}
                    isInvalid={isInvalid}
                    testId="invite-destination-picker"
                />
            )}
            {!readOnly && <Form.Text className="text-muted">{INVITE_DESTINATION_HINT}</Form.Text>}
            {errorText && (
                <div className="text-danger small mt-1" data-testid="target-entity-error">
                    {errorText}
                </div>
            )}
        </Form.Group>
    );
};

export default InviteDestinationField;
